import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';
import { ChatUiTripVoucherSchema } from '../../shared/schema/trip_voucher_schema.js';

/**
 * Stacked approval card (`approval_stack`) — a traveler → trip → voucher
 * hierarchy that REPLACES narrated approval text (D-02 / UX-02). It reuses the
 * trips card primitives (no new visual language): the voucher leaf is the trips
 * `ChatUiTripVoucher` shape verbatim, EXTENDED only with the deterministic
 * per-voucher arming (`decideChallengeId`).
 *
 * Determinism (UX-03/04): every approve/reject affordance carries a server-issued
 * session challenge armed by the agent at emit time — the LLM NEVER composes a
 * count or a decision. Per-voucher decisions echo `decideChallengeId`
 * (`approvals:decide`); each group (per-trip, per-traveler) and the top "all"
 * scope carry a `bulkChallengeId` / `allChallengeId` (`approvals:bulk-decide`).
 * The schema requires every challenge (`min(1)`) so an unarmed card fails
 * validation and is dropped at the render border.
 */

/** A voucher leaf — the trips voucher shape + the per-voucher decision arming. */
const ApprovalStackVoucherSchema = ChatUiTripVoucherSchema.extend({
  /** Per-voucher `approvals:decide` challenge (approve | reject). */
  decideChallengeId: z.string().min(1),
});
export type ApprovalStackVoucher = z.infer<typeof ApprovalStackVoucherSchema>;

/** A trip block under a traveler — name/dates/modals + per-trip bulk arming. */
const ApprovalStackTripGroupSchema = z.object({
  tripId: z.string(),
  tripName: z.string().nullable(),
  dates: z.object({
    startAt: z.string().nullable(),
    endAt: z.string().nullable(),
  }),
  modals: z.array(z.string()),
  count: z.number().int().min(0),
  totalAmount: z.number().nullable(),
  currency: z.string().nullable(),
  /** Per-trip `approvals:bulk-decide` challenge (scope `trip:<tripId>`). */
  bulkChallengeId: z.string().min(1),
  vouchers: z.array(ApprovalStackVoucherSchema),
});
export type ApprovalStackTripGroup = z.infer<typeof ApprovalStackTripGroupSchema>;

/** A traveler section — the primary grouping axis (UX-01). */
const ApprovalStackTravelerSchema = z.object({
  travelerId: z.string(),
  name: z.string(),
  count: z.number().int().min(0),
  totalAmount: z.number().nullable(),
  currency: z.string().nullable(),
  /** Per-traveler `approvals:bulk-decide` challenge (scope `traveler:<id>`). */
  bulkChallengeId: z.string().min(1),
  trips: z.array(ApprovalStackTripGroupSchema),
  /** Vouchers not grouped under a trip — decided under the traveler scope. */
  looseVouchers: z.array(ApprovalStackVoucherSchema),
});
export type ApprovalStackTraveler = z.infer<typeof ApprovalStackTravelerSchema>;

/** Producer cap — bounds the SSE frame for a heavy approver (mirror trips). */
export const CHAT_UI_MAX_APPROVAL_TRAVELERS = 20;

/** Membro `approval_stack` do `ChatUiSchema` (discriminatedUnion). */
export const ApprovalStackBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('approval_stack'),
    /** Top-level `approvals:bulk-decide` challenge (scope `all`). */
    allChallengeId: z.string().min(1),
    travelers: z.array(ApprovalStackTravelerSchema).max(CHAT_UI_MAX_APPROVAL_TRAVELERS),
  })
  .strict();
export type ApprovalStackBlock = z.infer<typeof ApprovalStackBlockSchema>;
